/**
 * Main Application Layout Component for Cedar Heights Music Academy Admin System
 * 
 * This component provides the core layout structure with:
 * - Responsive header with navigation
 * - Collapsible sidebar navigation
 * - Main content area
 * - Footer
 * - Role-based interface adaptation
 */

import React, { useState } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { AppHeader } from './AppHeader';
import { AppSidebar } from './AppSidebar';
import { AppFooter } from './AppFooter';
import { UserRole } from '../../types';

interface AppLayoutProps {
  userRole?: UserRole;
  onRoleSwitch?: (role: 'admin' | 'teacher') => void;
}

/**
 * Main application layout component that adapts to different screen sizes
 * and user roles, providing a consistent navigation experience.
 */
export const AppLayout: React.FC<AppLayoutProps> = ({
  userRole = 'admin',
  onRoleSwitch,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const handleSidebarToggle = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleSidebarClose = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  // Sidebar width constants
  const sidebarWidth = 280;
  const collapsedSidebarWidth = 64;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Header */}
      <AppHeader
        userRole={userRole}
        onRoleSwitch={onRoleSwitch}
        onMenuClick={handleSidebarToggle}
        sidebarOpen={sidebarOpen}
      />

      {/* Sidebar Navigation */}
      <AppSidebar
        userRole={userRole}
        open={sidebarOpen}
        onClose={handleSidebarClose}
        width={sidebarWidth}
        collapsedWidth={collapsedSidebarWidth}
        variant={isMobile ? 'temporary' : 'persistent'}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          // Remove margin-left since the sidebar is now properly positioned
          transition: theme.transitions.create(['margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        {/* Header Spacer */}
        <Box
          sx={{
            height: {
              xs: 56, // Mobile header height
              sm: 64, // Desktop header height
            },
          }}
        />

        {/* Page Content */}
        <Box
          sx={{
            flexGrow: 1,
            padding: theme.spacing(3),
            backgroundColor: theme.palette.background.default,
            minHeight: 'calc(100vh - 64px - 60px)', // Subtract header and footer height
          }}
        >
          <Outlet />
        </Box>

        {/* Footer */}
        <AppFooter />
      </Box>
    </Box>
  );
};

export default AppLayout;
